import { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { BannerContext } from '../../contexts/BannerContext';
import { useBilling } from '../../hooks/useBilling';
import { getPlanTier, isTrialExpired } from '../../utils/authAccess';
import { isPaymentFailed } from '../../utils/onboardingStatus';
import { setSignupPlan } from '../../utils/signupPlan';
import {
  buildTradePathWithProWelcome,
  getPlanActionLabel,
  getPlanChangeType,
  normalizePlanTier,
  setBillingCheckoutReturn,
} from './billingPlanData';

/**
 * Plan changes shared by /pricing and billing settings.
 * Track is free and never opens Stripe Checkout.
 * Trade starts as a 14-day trial with no card. Checkout runs only when the
 * person explicitly continues from billing, or the trial is already used up.
 * @param {'monthly'|'annual'} billingInterval
 */
export function useBillingPlanActions(billingInterval = 'monthly') {
  const { user, refreshUserSettings } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const navigate = useNavigate();
  const { startFreeTrial, activateFreeTrack, createCheckoutSession, createPortalSession, loading } = useBilling();
  const [ctaBusyTier, setCtaBusyTier] = useState(null);

  const subscription = user?.subscriptionStatus?.subscription;
  const hasAccess = user?.subscriptionStatus?.hasAccess === true;
  const hasStripeBilling = subscription?.hasStripeBilling === true;
  const isTrialing = subscription?.status === 'trialing';
  const trialExpired = isTrialExpired(user);
  const paymentFailed = isPaymentFailed(user);
  const activeTrial = isTrialing && hasAccess && !trialExpired;

  const startCheckout = useCallback(
    async (targetTier, cancelReturn = 'pricing') => {
      await createCheckoutSession(billingInterval, false, {
        planTier: normalizePlanTier(targetTier) || 'trade',
        cancelReturn,
      });
    },
    [billingInterval, createCheckoutSession]
  );

  const handlePlanAction = useCallback(
    async (targetTier, options = {}) => {
      const tier = normalizePlanTier(targetTier) || 'trade';
      const cancelReturn = options.cancelReturn || 'pricing';

      if (!user) {
        setSignupPlan(tier);
        navigate(`/register?redirect=/onboarding&plan=${tier}`);
        return;
      }

      setCtaBusyTier(tier);
      try {
        const hasUsedTrial = user?.subscriptionStatus?.hasUsedTrial === true;
        const currentTier = getPlanTier(user);

        if (tier === 'track') {
          if (hasStripeBilling) {
            await createPortalSession();
            return;
          }
          await activateFreeTrack();
          if (refreshUserSettings) await refreshUserSettings();
          showMessage?.('You are on Track. Connect a broker when you are ready.', 'success');
          const brokerConnected = user?.hasBrokerConnection || user?.hasTradeStationCredentials;
          if (!brokerConnected) navigate('/onboarding');
          return;
        }

        const canStartNoCardTrial = !hasStripeBilling && !hasUsedTrial && !trialExpired && !paymentFailed;
        const alreadyOnTrade = currentTier === 'trade' && hasAccess && !trialExpired;

        if (canStartNoCardTrial && !alreadyOnTrade) {
          await startFreeTrial('trade');
          if (refreshUserSettings) await refreshUserSettings();
          showMessage?.('Your 14-day Trade trial is on. No card required.', 'success');
          if (options.postPlanChangeNav) navigate(buildTradePathWithProWelcome());
          else if (!hasAccess) navigate('/onboarding');
          return;
        }

        if (!hasAccess && !hasUsedTrial && !trialExpired && !paymentFailed) {
          setSignupPlan('trade');
          navigate('/onboarding');
          return;
        }

        if (paymentFailed && hasStripeBilling) {
          await createPortalSession();
          return;
        }

        if (options.postPlanChangeNav) {
          setBillingCheckoutReturn({
            change: getPlanChangeType(currentTier, 'trade'),
            targetTier: 'trade',
            source: options.checkoutSource || 'pricing',
            returnPath: options.checkoutReturnPath,
          });
        }
        await startCheckout('trade', cancelReturn);
      } catch (e) {
        console.error('Billing plan action error:', e);
        const stripeBilling = e?.status === 409 || e?.error === 'stripe_billing';
        if (stripeBilling) {
          try {
            await createPortalSession();
            return;
          } catch (portalError) {
            console.error('Billing portal error:', portalError);
          }
        }
        showMessage?.(
          e?.message || 'Could not update your plan. Please try again.',
          'danger'
        );
      } finally {
        setCtaBusyTier(null);
      }
    },
    [
      user,
      navigate,
      hasStripeBilling,
      hasAccess,
      trialExpired,
      paymentFailed,
      startFreeTrial,
      activateFreeTrack,
      refreshUserSettings,
      createPortalSession,
      startCheckout,
      showMessage,
    ]
  );

  const getCtaState = useCallback(
    (tierInput) => {
      const tier = normalizePlanTier(tierInput) || 'trade';
      const currentPlanTier = getPlanTier(user);
      const hasUsedTrial = user?.subscriptionStatus?.hasUsedTrial === true;
      const isCurrentPlan = hasAccess && !trialExpired && currentPlanTier === tier;
      const isDifferentTier = currentPlanTier !== tier;
      const needsSubscribe = trialExpired || paymentFailed || (hasUsedTrial && !hasAccess);
      const canStartTradeTrial = Boolean(
        user && tier === 'trade' && !hasStripeBilling && !hasUsedTrial && !trialExpired && !paymentFailed && isDifferentTier
      );
      const canSwitchToTrack = Boolean(user && tier === 'track' && !isCurrentPlan && !hasStripeBilling);
      const canCheckoutEarly = Boolean(user && isCurrentPlan && activeTrial && !paymentFailed && tier === 'trade');
      const canSwitchActivePlan =
        Boolean(user) && hasAccess && !trialExpired && !paymentFailed && isDifferentTier && tier === 'trade' && !canStartTradeTrial;
      const showPaidAction =
        Boolean(user) &&
        tier === 'trade' &&
        (canCheckoutEarly ||
          canSwitchActivePlan ||
          (needsSubscribe && (isDifferentTier || tier === currentPlanTier)));
      const showPlanAction = showPaidAction || canStartTradeTrial || canSwitchToTrack;

      let actionLabel = getPlanActionLabel(currentPlanTier, tier);
      if (canCheckoutEarly) actionLabel = 'Checkout';
      else if (canStartTradeTrial) actionLabel = 'Start 14-day trial';
      else if (canSwitchToTrack) actionLabel = 'Switch to Track';
      else if (canSwitchActivePlan) actionLabel = 'Switch plan';

      return {
        isCurrentPlan,
        canCheckoutEarly,
        showPaidAction,
        showPlanAction,
        busy: ctaBusyTier === tier || loading,
        currentPlanTier,
        actionLabel,
      };
    },
    [user, hasAccess, trialExpired, activeTrial, hasStripeBilling, paymentFailed, ctaBusyTier, loading]
  );

  return {
    handlePlanAction,
    getCtaState,
    billingLoading: loading,
    startCheckout,
  };
}
